import { DocumentSignerReq } from "./DocumentSign.dto";
import { DocumentEntry, FolderFileEntryAndRole, FolderSummary, FolderType, ProposalEntry, SignEntry } from "./Folder.dto";
import { ConvertQueue } from "./pdf/ConvertQueue.dto";
import { DocsPdfPage } from "./pdf/DocsPdf.dto";
import { UserEssential } from "../sso/User.dto";

export class FolderFileCriteria {
  static FOLDER_FILE_MAX_CHILDREN = 100;
  static FOLDER_FILE_NAME_MAXLEN = 256;
  static FOLDER_FILE_VERSION_MAXLEN = 6;
  static FOLDER_FILE_MAX_UPLOAD_LIMIT_IN_TEN_MIB = 10;
  static FOLDER_FILE_MAX_UPLOAD_LIMITLESS_IN_MIB = 235;
  static FOLDER_FILE_BULK_UPLOAD_MAX_COUNT = 20;
  static FOLDER_FILE_VERSION_REGEX = /^(([1-9][0-9]*)|0)(\.(([1-9][0-9]*)|0){1,})+$/;
  static FOLDER_FILE_UPLOAD_ACCEPT = ".pdf";
  static FOLDER_FILE_ARCHIVER_UPLOAD_ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx";
  static FOLDER_FILE_DEFAULT_VERSION = "1.0";
  static FOLDER_FILE_DOWNLOAD_NAME_ELLIPSE_THRESHOLD_LEN = 25;
}

export enum FileValidationStatus {
  Pass = "PASS",
  ExtensionInvalid = "EXTENSION_INVALID",
  MaxSizeExceeded = "MAX_SIZE_EXCEEDED",
  Encrypted = "ENCRYPTED",
}

export enum FolderFileType {
  Document = "DOCUMENT",
  Proposal = "PROPOSAL",
  Sign = "SIGN",
}

export enum FileType {
  Png = "PNG",
  Jpg = "JPG",
  Pdf = "PDF",
  Doc = "DOC",
  Ppt = "PPT",
  Xls = "XLS",
  Docx = "DOCX",
  Pptx = "PPTX",
  Xlsx = "XLSX",
  Xml = "XML",
  Zip = "ZIP",
  Dcm = "DCM",
  Bmp = "BMP",
  Tiff = "TIFF",
  Txt = "TXT",
  Unknown = "UNKNOWN",
}

export interface FolderFile {
  id: bigint;
  organizationId: bigint;
  folderId: bigint;
  type: FolderFileType;
  name: string;
  size: number;
  folderType: FolderType;
  isDestroyed: boolean;
}

export interface FolderFileResource extends FolderFile {
  createdBy: UserEssential;
  createdAt: Date;
}

export interface CreatableDocumentFile {
  documentId?: bigint;
  title: string;
  version: string;
  contentBucket: string;
  contentKey: string;
  contentName?: string;
}

export interface CreateDocumentFileRequest {
  organizationId: bigint;
  folderId?: bigint;
  title: string;
  version: string;
  contentFileBucket: string;
  contentFileKey: string;
  contentFileName: string;
  folderType?: FolderType;
}

export interface CreateDocumentFilesRequest {
  organizationId: bigint;
  folderId?: bigint;
  files: CreatableDocumentFile[];
  folderType?: FolderType;
  isInvalidFileSkippable?: boolean;
}

export interface CreateDocumentFileResponse {
  entry: DocumentEntry;
}

export interface CreatedDocumentFile {
  fileId: bigint;
  documentId: bigint;
}

export interface InvalidDocumentFile {
  index: string;
  status: FileValidationStatus;
}

export interface CreateDocumentFilesResponse {
  entries: CreatedDocumentFile[];
  invalidFiles: InvalidDocumentFile[];
}

export interface CreateProposalFileRequest {
  organizationId: bigint;
  folderId?: bigint;
  templateId: bigint;
  title: string;
  version: string;
}

export interface CreateProposalFileResponse {
  entry: ProposalEntry;
}

export interface CreateSignFileRequest {
  organizationId: bigint;
  folderId?: bigint;
  documentFileId: bigint;
  title: string;
  version: string;
  signers: DocumentSignerReq[];
  isGxpCompatible: boolean;
  folderType?: FolderType;
}

export interface CreateSignFileResponse {
  entry: SignEntry;
}

export interface GetFolderFileByIdRequest {
  fileId: bigint;
}

export interface GetFolderFileByIdResponse {
  folders: FolderSummary[];
  file: FolderFileEntryAndRole;
}

export interface DownloadFolderFileRequest {
  fileId: bigint;
}

export interface DownloadFolderFileResponse {
  url: string;
}

export interface GetFolderFileDocsPdfPageRequest {
  fileId: bigint;
  docsPdfId: bigint;
  page: number;
}

export interface GetFolderFileDocsPdfPageResponse {
  docsPdfPage: DocsPdfPage;
}

export interface RemoveFolderFileByIdRequest {
  fileId: bigint;
}

export interface RemoveFolderFileByIdResponse {
  removed: boolean;
}

export interface MoveFolderFileByIdRequest {
  fileId: bigint;
  destinationFolderId: bigint | null;
}

export interface MoveFolderFileByIdResponse {
  moved: boolean;
}

export interface RenameFolderFileByIdRequest {
  fileId: bigint;
  name: string;
}

export interface RenameFolderFileByIdResponse {
  renamed: boolean;
}

export interface StorageUsage {
  total: number;
  usage: number;
}

export interface GetFolderFilePdfConvertQueueRequest {
  fileId: bigint;
}

export interface GetFolderFilePdfConvertQueueResponse {
  convertQueue: ConvertQueue;
}

export interface RetryFolderFileConvertPdfRequest {
  fileId: bigint;
}

export interface RetryFolderFileConvertPdfResponse {
  result: boolean;
}
